import axios from 'axios';
import { toast } from 'react-toastify';

// ===== AXIOS INSTANCE WITH INTERCEPTORS =====
const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to every request automatically
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global error handler
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || 'Request failed';
    console.error('API Error:', message);
    toast.error(message);
    return Promise.reject(error);
  }
);

// ===== HELPER FUNCTION FOR SAFE API CALLS =====
const safeApiCall = async (apiFunction, errorMessage = 'Operation failed') => {
  try {
    const response = await apiFunction();
    return { data: response.data, error: null };
  } catch (error) {
    console.error(errorMessage, error);
    return { data: null, error };
  }
};

// ===== REVIEW SERVICES =====
export const reviewService = {
  // Get reviews for a candidate
  getReviews: async (candidateId) => {
    return safeApiCall(
      () => apiClient.post(process.env.REACT_APP_CLIENT_CANDIDATEREVIEWS, { candidateId }),
      'Failed to fetch reviews'
    );
  },

  // Submit a new review
  submitReview: async (reviewData) => {
    return safeApiCall(
      () => apiClient.post(process.env.REACT_APP_CLIENT_CANDIDATEREVIEW, reviewData),
      'Failed to submit review'
    );
  },

  // Get candidate profile with reviews
  getCandidateProfile: async (candidateId) => {
    return safeApiCall(
      () => apiClient.post(process.env.REACT_APP_CLIENT_CANDIDATEPROFILE, { candidateId }),
      'Failed to fetch candidate profile'
    );
  },
};

// ===== UPLOAD SERVICES =====
export const uploadService = {
  // Get pre-signed URL for S3 upload
  getPreSignedUrl: async (key) => {
    return safeApiCall(
      () => apiClient.post(process.env.REACT_APP_CLIENT_GETPRESIGNEDURL, { key }),
      'Failed to get upload URL'
    );
  },

  // Upload file to S3 using pre-signed URL
  uploadToS3: async (presignedUrl, file) => {
    try {
      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': file.type },
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('File upload failed');
      return { success: false, error };
    }
  },
};

// ===== METRICS SERVICES =====
export const metricsService = {
  // Get task metrics for talent
  getTaskMetrics: async (talentId, podId) => {
    return safeApiCall(
      () =>
        apiClient.post(process.env.REACT_APP_TALENT_PODTASKSMETRICSTALENT, {
          talent_id: talentId,
          pod_id: podId,
        }),
      'Failed to fetch metrics'
    );
  },
};

// ===== CANDIDATE SERVICES =====
export const candidateService = {
  // Get candidate details
  getCandidateDetails: async (candidateId) => {
    return safeApiCall(
      () => apiClient.get(`${process.env.REACT_APP_CANDIDATE_DETAILS}/${candidateId}`),
      'Failed to fetch candidate details'
    );
  },

  // Update candidate profile
  updateCandidate: async (candidateData) => {
    return safeApiCall(
      () => apiClient.post(process.env.REACT_APP_UPDATE_CANDIDATE, candidateData),
      'Failed to update candidate'
    );
  },
};

// Export the base client for custom requests
export default apiClient;